import React from "react";
import { RefreshCw, CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { clp } from "../../lib/informeTributario/engine";

const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const CATEGORIAS_GASTO = ["sin_iva_probable", "otros", "depreciacion"];

const fmtISO = (s) => (s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : "");

function Fila({ titulo, valor, detalle, accion, aplicado, onAplicar, disabled }) {
  return (
    <div className="py-2 border-b border-border/60 last:border-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-foreground">{titulo}</div>
          {detalle && <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">{detalle}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[12px] font-semibold tabular-nums text-foreground">{valor}</div>
          {onAplicar && (
            aplicado ? (
              <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" /> aplicado
              </span>
            ) : (
              <button
                type="button"
                onClick={onAplicar}
                disabled={disabled}
                className="text-[10.5px] font-semibold text-primary hover:underline disabled:opacity-50"
              >
                {accion || "Aplicar"}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function EstadoConc({ r, etiqueta }) {
  if (!r?.disponible) {
    return (
      <li className="flex items-center gap-1.5 text-muted-foreground">
        <MinusCircle className="h-3.5 w-3.5 shrink-0" /> {etiqueta}: sin registros
      </li>
    );
  }
  const ok = r.difs.length === 0;
  return (
    <li className={`flex items-start gap-1.5 ${ok ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-px" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />}
      <span>
        {etiqueta}: {ok ? "cuadra" : `diferencias en ${r.difs.join(", ")}`}
        {r.sinDatos.length > 0 && <span className="text-muted-foreground"> · sin registros en {r.sinDatos.join(", ")}</span>}
      </span>
    </li>
  );
}

export default function PanelERP({ X, S, M, P, setP, loading, error, onRefresh, bloqueado, readOnly }) {
  if (bloqueado) {
    return (
      <p className="text-[12px] text-destructive">
        No se cargan datos de Aequor porque la carpeta no corresponde a la empresa activa.
      </p>
    );
  }
  if (loading && !X) return <p className="text-[12px] text-muted-foreground">Cargando datos de Aequor…</p>;
  if (error && !X) {
    return (
      <div className="text-[12px] text-destructive space-y-2">
        <p>{error}</p>
        <button type="button" onClick={onRefresh} className="text-primary font-semibold hover:underline">Reintentar</button>
      </div>
    );
  }
  if (!X || !S) return null;

  const set = (patch) => setP((prev) => ({ ...prev, ...patch }));
  const R = M?.concERP?.resumen;
  const conta = S.contabilidad;
  const gastos = S.gastos.egresos.filter((e) => CATEGORIAS_GASTO.includes(e.categoria));
  const seleccion = P.gastosSel || S.gastos.seleccion;
  const hastaTxt = S.hastaMes ? `enero a ${MES_CORTO[S.hastaMes - 1]}` : "";
  const prebalance = conta
    ? {
        caja: conta.caja, clientes: conta.clientes, activoFijoNeto: conta.activoFijoNeto, proveedores: conta.proveedores,
        prestamos: conta.prestamos, capitalPagado: conta.capitalPagado, ventasContables: conta.ventas, gastosContables: conta.gastos,
      }
    : null;
  const prebalanceAplicado = prebalance && Object.entries(prebalance).every(([k, v]) => +P[k] === +v && P[k] !== "");

  const toggleGasto = (codigo) => {
    const next = seleccion.includes(codigo) ? seleccion.filter((c) => c !== codigo) : [...seleccion, codigo];
    const acum = S.gastos.egresos.filter((e) => next.includes(e.codigo)).reduce((s, e) => s + e.acumulado, 0);
    set({ gastosSel: next, gastosSinIVA: S.hastaMes ? Math.round((acum * 12) / S.hastaMes) : 0 });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>Datos al {fmtISO(X.hasta)}{loading ? " · actualizando…" : ""}</span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1 text-primary font-semibold hover:underline disabled:opacity-50"
          data-testid="informe-erp-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      <div>
        <Fila
          titulo="Honorarios"
          valor={S.hay.hon ? clp(S.honorarios.bruto) : "—"}
          detalle={
            S.hay.hon
              ? `${S.honorarios.docs} boletas (${hastaTxt}) · retención ${clp(S.honorarios.retencion)}`
              : "Sin boletas en el libro de honorarios: se estiman desde la retención del F29."
          }
        />
        {S.hay.hon && (
          <label className="flex items-center gap-2 text-[11.5px] text-foreground pb-2">
            <input
              type="checkbox"
              checked={P.fuenteHonorarios === "erp"}
              disabled={readOnly}
              onChange={(e) => set({ fuenteHonorarios: e.target.checked ? "erp" : "f29" })}
            />
            Usar el libro de honorarios en la proyección
          </label>
        )}
        {M?.honorariosConflicto && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400 pb-2">
            El libro no cuadra con el código 151 (V01): la proyección usa la estimación desde el F29.
          </p>
        )}

        <Fila
          titulo="Remuneraciones (costo empresa)"
          valor={S.hay.rem ? clp(S.remuneraciones.anual) : "—"}
          detalle={
            S.hay.rem
              ? `${S.remuneraciones.meses} meses liquidados · acumulado ${clp(S.remuneraciones.acumulado)} · anualizado con el promedio de los últimos meses`
              : "Sin liquidaciones en el año."
          }
          onAplicar={S.hay.rem && !readOnly ? () => set({ remuneraciones: S.remuneraciones.anual }) : null}
          aplicado={S.hay.rem && +P.remuneraciones === S.remuneraciones.anual}
        />

        {conta && (
          <div className="py-2 border-b border-border/60">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[12px] font-medium text-foreground">Gastos sin IVA (libro mayor)</div>
                <div className="text-[11px] text-muted-foreground">Marca las cuentas que no pasan por facturas de compra. Se anualiza ({hastaTxt}).</div>
              </div>
              <div className="text-[12px] font-semibold tabular-nums shrink-0">{clp(S.gastos.anual)}</div>
            </div>
            {gastos.length === 0 ? (
              <p className="text-[11px] text-muted-foreground mt-1">Sin cuentas de gasto con movimiento.</p>
            ) : (
              <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto pr-1">
                {gastos.map((g) => (
                  <li key={g.codigo}>
                    <label className="flex items-center gap-2 text-[11.5px]">
                      <input
                        type="checkbox"
                        checked={seleccion.includes(g.codigo)}
                        disabled={readOnly}
                        onChange={() => toggleGasto(g.codigo)}
                      />
                      <span className="flex-1 min-w-0 truncate">
                        <span className="font-mono text-muted-foreground">{g.codigo}</span> {g.nombre}
                      </span>
                      <span className="tabular-nums text-muted-foreground">{clp(g.acumulado)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Fila
          titulo="Prebalance y conciliación de ventas"
          valor={conta ? clp(conta.caja) : "—"}
          detalle={
            conta
              ? `Caja y bancos al corte · ${conta.asientos} asientos registrados en el año · ventas contables ${clp(conta.ventas)}`
              : "Sin asientos registrados en el año: el prebalance se completa a mano."
          }
          accion="Aplicar al prebalance"
          onAplicar={conta && !readOnly ? () => set(prebalance) : null}
          aplicado={prebalanceAplicado}
        />
      </div>

      {R && (
        <div>
          <div className="label-caps mb-1">F29 vs registros</div>
          <ul className="space-y-1 text-[11.5px]">
            <EstadoConc r={R.ventas} etiqueta="Ventas netas" />
            <EstadoConc r={R.debito} etiqueta="IVA débito" />
            <EstadoConc r={R.credito} etiqueta="IVA crédito" />
            <EstadoConc r={R.ret} etiqueta="Retención honorarios" />
            <EstadoConc r={R.impUnico} etiqueta="Impuesto único" />
          </ul>
          <p className="text-[11px] text-muted-foreground mt-1.5">El detalle mes a mes está en la hoja de trabajo.</p>
        </div>
      )}
    </div>
  );
}
